/**
 * Mobile Edit Mode Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles loading and populating transaction data in edit mode
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../../firebase";
import { Transaction, Account, TransactionCategory, TransactionCategoryGroup, TransactionCategoryDetail, TransactionCustomField } from "../../../../types";
import { api } from "../../../../services/api";
import { mergeNoteAndAttachmentIntoCustomFieldValues } from "../../../../utils/transactionCustomFieldsDerivation";

// Local types matching CreateTransactionMobile.tsx
interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: Record<string, string | { url: string; name: string; type: string }>;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: Record<string, string | { url: string; name: string; type: string }>;
  customFieldPendingFiles?: Record<string, File>;
}

export interface UseMobileEditModeProps {
  isEditMode: boolean;
  transactionId: string | undefined;
  workspaceId: string;
  workspaceSlug: string | undefined;
  transactionType: "expense" | "income" | "transfer";
  accounts: Account[];
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  defaultCurrency: string;
  editingTransaction: Transaction | null;
  setEditingTransaction: React.Dispatch<React.SetStateAction<Transaction | null>>;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  transferFormData: TransferFormData;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  setSelectedCategoryDetails: (details: TransactionCategoryDetail[]) => void;
  setCalendarViewDate: React.Dispatch<React.SetStateAction<Date>>;
  setTimeWasEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setRateManuallyEdited: (edited: boolean) => void;
  setDefaultCurrencyRateManuallyEdited: (edited: boolean) => void;
  setInitialFormData: (data: FormData | null) => void;
  setInitialTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData | null>>;
  setCalculatorExpression: (value: string) => void;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
  formatDateLocal: (date: Date) => string;
  formatTimeLocal: (date: Date) => string;
  navigate: ReturnType<typeof useNavigate>;
  setShowTransferFee?: (show: boolean) => void;
  transactions?: Transaction[];
  /** When opening edit from list tap, pass the list transaction so we can show form immediately without API fetch */
  initialTransaction?: Transaction | null;
  transactionCustomFields?: TransactionCustomField[];
}

export function useMobileEditMode({
  isEditMode,
  transactionId,
  workspaceId,
  workspaceSlug,
  transactionType,
  accounts,
  expenseCategories,
  incomeCategories,
  categoryGroups,
  defaultCurrency,
  editingTransaction,
  setEditingTransaction,
  formData,
  setFormData,
  transferFormData,
  setTransferFormData,
  setSelectedCategoryDetails,
  setCalendarViewDate,
    setTimeWasEnabled,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    setInitialFormData,
    setInitialTransferFormData,
    setCalculatorExpression,
    calculateTransferRate,
  formatDateLocal,
  formatTimeLocal,
  navigate,
  setShowTransferFee,
  transactions = [],
  initialTransaction,
  transactionCustomFields = [],
}: UseMobileEditModeProps) {
  useEffect(() => {
    if (!isEditMode || !transactionId || !workspaceId) return;

    // Don't reload if we already have the transaction (e.g. from a previous run)
    if (editingTransaction && editingTransaction.id === transactionId) return;

    // Helper function to parse date from API or list (handles Firestore Timestamp, Date, or ISO string)
    const parseTransactionDate = (dateValue: any): Date => {
          if (!dateValue) {
            return new Date();
          }
          
          // Check if it has a toDate method (Firestore Timestamp object that wasn't serialized)
          if (dateValue && typeof dateValue.toDate === 'function') {
            const parsedDate = dateValue.toDate();
            return parsedDate;
          }
          
          // Check if it's a Firestore Timestamp object (can have seconds or _seconds property)
          // When Firestore Timestamps are serialized to JSON, they use _seconds and _nanoseconds
          const seconds = dateValue.seconds !== undefined ? dateValue.seconds : dateValue._seconds;
          const nanoseconds = dateValue.nanoseconds !== undefined ? dateValue.nanoseconds : (dateValue._nanoseconds || 0);
          
          if (seconds !== undefined) {
            const parsedDate = new Date(seconds * 1000 + nanoseconds / 1000000);
            return parsedDate;
          }
          
          // If it's already a Date object
          if (dateValue instanceof Date) {
            return dateValue;
          }
          
          // If it's an ISO string
          if (typeof dateValue === 'string') {
            const parsedDate = new Date(dateValue);
            if (!isNaN(parsedDate.getTime())) {
              return parsedDate;
            }
          }
          
          // Try to parse as number (timestamp in milliseconds)
          if (typeof dateValue === 'number') {
            const parsedDate = new Date(dateValue);
            return parsedDate;
          }
          
          // Fallback - return current date
          return new Date();
        };

    const populateFormFromTransaction = (transaction: Transaction): void => {
      const actualType = transaction.type === "transfer"
        ? "transfer"
        : transaction.isIncome
          ? "income"
          : "expense";
      if (actualType !== transactionType) {
        navigate(`/${workspaceSlug}/edit-transaction/${actualType}/${transactionId}`, { replace: true });
        return;
      }

      // Populate form data based on transaction type
      if (transaction.type === "transfer") {
          const transactionDate = parseTransactionDate(transaction.date);
          const transactionTime = formatTimeLocal(transactionDate);
          
          // Check if time is midnight (00:00:00) - this indicates no time was set
          // If it's exactly midnight with zero seconds and milliseconds, treat as "no time"
          const hours = transactionDate.getHours();
          const minutes = transactionDate.getMinutes();
          const seconds = transactionDate.getSeconds();
          const milliseconds = transactionDate.getMilliseconds();
          const isMidnight = hours === 0 && minutes === 0 && seconds === 0 && milliseconds === 0;
          
          // If it's midnight, show empty time (no time was set)
          // Otherwise, show the actual time that was selected
          const displayTime = isMidnight ? "" : transactionTime;
          
          
          // Always show time field when editing to display the actual time
          setTimeWasEnabled(true);
          
          // Set calendar view to transaction date (ensure it's valid)
          if (!isNaN(transactionDate.getTime())) {
            setCalendarViewDate(transactionDate);
          }
          
          // Populate form data immediately without waiting for fee data
          // This ensures fields are filled as soon as possible
          const transferAmount = transaction.amount?.toString() || "";
          const initialTransfer = {
            fromAccountId: transaction.fromAccountId || "",
            toAccountId: transaction.toAccountId || "",
            amount: transferAmount,
            date: formatDateLocal(transactionDate),
            time: displayTime,
            transferRate: transaction.transferRate?.toString() || "",
            feeAmount: undefined as string | undefined,
            feeAccountId: undefined as string | undefined,
            customFieldValues: mergeNoteAndAttachmentIntoCustomFieldValues(
              transactionCustomFields,
              "transfer",
              (transaction as any).customFieldValues,
              transaction.note,
              transaction.attachmentUrl ?? null,
              transaction.attachmentName ?? null,
              transaction.attachmentType ?? null
            ),
            customFieldPendingFiles: undefined as Record<string, File> | undefined,
          };
          setTransferFormData(initialTransfer);
          setInitialTransferFormData(initialTransfer);
          setCalculatorExpression(transferAmount || "0");

          // Find fee transaction linked to this transfer asynchronously
          // Try to find fee in provided transactions first (synchronous check)
          let feeAmount: string | undefined;
          let feeAccountId: string | undefined;
          
          if (transactions && transactions.length > 0) {
            const feeTransaction = transactions.find((t) => {
              const hasTransferFeeFor = (t as any).transferFeeFor === transaction.id;
              const isTransferFee = t.title === "Transfer Fee" || t.category === "Transfer Fee";
              const isExpense = t.type === "expense";
              return hasTransferFeeFor && isTransferFee && isExpense;
            });
            
            if (feeTransaction) {
              feeAmount = feeTransaction.amount?.toString();
              feeAccountId = feeTransaction.accountId;
              
              // Update form data and initial snapshot so change detection doesn't treat fee load as a change
              setTransferFormData((prev) => ({
                ...prev,
                feeAmount: feeAmount,
                feeAccountId: feeAccountId,
              }));
              setInitialTransferFormData((prev) => prev ? { ...prev, feeAmount, feeAccountId } : null);
              
              if (setShowTransferFee) {
                setShowTransferFee(true);
              }
            }
          }
          
          // If not found in provided transactions, fetch fee from Firestore (same as desktop)
          // so we reliably load the linked fee when editing a transfer on mobile
          if (feeAmount === undefined || !feeAccountId) {
            (async () => {
              try {
                const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
                const feeQuery = query(
                  transactionsRef,
                  where("transferFeeFor", "==", transaction.id),
                  where("type", "==", "expense")
                );
                const feeQuerySnapshot = await getDocs(feeQuery);

                if (!feeQuerySnapshot.empty) {
                  const feeDoc = feeQuerySnapshot.docs[0];
                  const feeData = feeDoc.data();
                  const foundFeeAmount = feeData.amount != null ? String(feeData.amount) : undefined;
                  const foundFeeAccountId = feeData.accountId || undefined;

                  if (foundFeeAmount !== undefined && foundFeeAccountId) {
                    setTransferFormData((prev) => ({
                      ...prev,
                      feeAmount: foundFeeAmount,
                      feeAccountId: foundFeeAccountId,
                    }));
                    setInitialTransferFormData((prev) => prev ? { ...prev, feeAmount: foundFeeAmount, feeAccountId: foundFeeAccountId } : null);

                    if (setShowTransferFee) {
                      setShowTransferFee(true);
                    }
                  }
                }
              } catch (error) {
                console.error("Error loading fee data:", error);
              }
            })();
          }
        } else {
          // Expense/Income transaction
          const transactionDate = parseTransactionDate(transaction.date);
          const transactionTime = formatTimeLocal(transactionDate);
          
          // Check if time is midnight (00:00:00) - this indicates no time was set
          // If it's exactly midnight with zero seconds and milliseconds, treat as "no time"
          const hours = transactionDate.getHours();
          const minutes = transactionDate.getMinutes();
          const seconds = transactionDate.getSeconds();
          const milliseconds = transactionDate.getMilliseconds();
          const isMidnight = hours === 0 && minutes === 0 && seconds === 0 && milliseconds === 0;
          
          // If it's midnight, show empty time (no time was set)
          // Otherwise, show the actual time that was selected
          const displayTime = isMidnight ? "" : transactionTime;
          
          
          // Get account info if available, otherwise use defaults
          const account = accounts.length > 0 ? accounts.find(acc => acc.id === transaction.accountId) : null;
          const accountCurrency = account?.currency || transaction.currency || defaultCurrency;
          const transactionCurrency = transaction.currency || accountCurrency;
          
          // Load category details if category exists and categories are loaded
          const categoryName = transaction.category || "";
          let details: TransactionCategoryDetail[] = [];
          
          // Only try to load category details if categories are available
          if ((transaction.isIncome ? incomeCategories : expenseCategories).length > 0) {
            const categories = transaction.isIncome ? incomeCategories : expenseCategories;
            const selectedCategory = categories.find((cat) => cat.name === categoryName);
            const group = selectedCategory?.groupId ? categoryGroups.find((g) => g.id === selectedCategory.groupId) : null;
            const allGroupDetails = group?.details || [];
            const enabledDetailNames = selectedCategory?.enabledDetails && Array.isArray(selectedCategory.enabledDetails)
              ? selectedCategory.enabledDetails 
              : [];
            details = enabledDetailNames.length > 0 
              ? allGroupDetails.filter((detail: TransactionCategoryDetail) => enabledDetailNames.includes(detail.name))
              : [];
          }
          
          setSelectedCategoryDetails(details);
          
          // Set calendar view to transaction date (ensure it's valid)
          if (!isNaN(transactionDate.getTime())) {
            setCalendarViewDate(transactionDate);
          }
          
          // Always show time field when editing to display the actual time
          setTimeWasEnabled(true);
          
          const existingRate = transaction.rate?.toString() || "";
          // Use stored defaultCurrencyRate if available, otherwise calculate from amountInDefaultCurrency
          // DO NOT auto-fill with current setting value - only use saved value or calculated from saved amountInDefaultCurrency
          let existingDefaultCurrencyRate = "";

          // Use stored defaultCurrencyRate only when it's not the "same-currency" 1 (which becomes wrong after default currency change).
          // On mobile, defaultCurrency can be stale in closures; always treat stored rate 1 as stale and use settings rate (match desktop).
          const storedRateRaw = transaction.defaultCurrencyRate;
          const storedRateNum = storedRateRaw !== undefined && storedRateRaw !== null
            ? (typeof storedRateRaw === "number" ? storedRateRaw : parseFloat(String(storedRateRaw)))
            : NaN;
          const isStoredRateOne = !isNaN(storedRateNum) && storedRateNum > 0 && Math.abs(storedRateNum - 1) < 1e-6;

          if (!isStoredRateOne && transaction.defaultCurrencyRate !== undefined && transaction.defaultCurrencyRate !== null) {
            const rawRate = transaction.defaultCurrencyRate;
            const rateValue = typeof rawRate === "number" ? rawRate : parseFloat(String(rawRate));
            if (!isNaN(rateValue) && rateValue > 0) {
              // Stored rate is expense-to-default, but field needs account-to-default
              const exchangeRate = transaction.rate || 1;
              const accountToDefaultRate = exchangeRate > 0 ? rateValue / exchangeRate : rateValue;
              const rounded = Math.round(accountToDefaultRate * 100000) / 100000;
              existingDefaultCurrencyRate = rounded % 1 === 0
                ? rounded.toString()
                : rounded.toFixed(5).replace(/\.?0+$/, "");
            }
          }
          // If no stored rate, try to calculate from amountInDefaultCurrency only when default currency hasn't changed.
          if (!existingDefaultCurrencyRate && transaction.defaultCurrencyAtTime && transaction.defaultCurrencyAtTime === defaultCurrency && transaction.amountInDefaultCurrency && transaction.amount) {
            if (transactionCurrency !== defaultCurrency && accountCurrency !== defaultCurrency) {
              const exchangeRate = transaction.rate || 1;
              const accountAmount = transaction.amount * exchangeRate;
              const accountToDefaultRate = accountAmount > 0 ? transaction.amountInDefaultCurrency / accountAmount : 0;
              if (accountToDefaultRate > 0) {
                const rounded = Math.round(accountToDefaultRate * 100000) / 100000;
                existingDefaultCurrencyRate = rounded % 1 === 0
                  ? rounded.toString()
                  : rounded.toFixed(5).replace(/\.?0+$/, "");
              }
            }
          }

          // Use current settings rate when: no stored rate, or stored rate was 1 and default changed / transaction currency differs (match desktop).
          // Only set if calculated rate is not 1 when currencies differ (rate 1 means rates not loaded yet - leave empty so useMobileRateCalculations can fill when ready).
          if (!existingDefaultCurrencyRate && accountCurrency !== defaultCurrency) {
            const calculatedAccountToDefaultRate = calculateTransferRate(accountCurrency, defaultCurrency);
            const isRateLoaded = Math.abs(calculatedAccountToDefaultRate - 1) >= 1e-6;
            if (isRateLoaded) {
              const rounded = Math.round(calculatedAccountToDefaultRate * 100000) / 100000;
              existingDefaultCurrencyRate = rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(5).replace(/\.?0+$/, "");
            }
          }

          // Safety net: if we ended up with rate 1 but account currency differs from default, use settings rate only when it's actually loaded (not 1)
          const finalRateNum = existingDefaultCurrencyRate ? parseFloat(existingDefaultCurrencyRate) : NaN;
          if (accountCurrency !== defaultCurrency && !isNaN(finalRateNum) && finalRateNum > 0 && Math.abs(finalRateNum - 1) < 1e-6) {
            const calculatedAccountToDefaultRate = calculateTransferRate(accountCurrency, defaultCurrency);
            const isRateLoaded = Math.abs(calculatedAccountToDefaultRate - 1) >= 1e-6;
            if (isRateLoaded) {
              const rounded = Math.round(calculatedAccountToDefaultRate * 100000) / 100000;
              existingDefaultCurrencyRate = rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(5).replace(/\.?0+$/, "");
            } else {
              // Rates not loaded yet (e.g. opened from list before currencies loaded) - leave empty so effect can fill when ready
              existingDefaultCurrencyRate = "";
            }
          }

          // Detect if rates were manually edited by comparing with calculated rates
          if (existingRate && transactionCurrency !== accountCurrency) {
            const calculatedRate = calculateTransferRate(transactionCurrency, accountCurrency);
            const storedRate = parseFloat(existingRate);
            if (!isNaN(storedRate) && storedRate > 0) {
              const rateDifference = Math.abs(storedRate - calculatedRate) / Math.max(storedRate, calculatedRate);
              // If rate differs by more than 0.1%, assume it was manually edited
              if (rateDifference > 0.001) {
                setRateManuallyEdited(true);
              } else {
                setRateManuallyEdited(false);
              }
            } else {
              setRateManuallyEdited(false);
            }
          } else {
            setRateManuallyEdited(false);
          }
          
          // Same logic for default currency rate (field stores account-to-default; compare with account-to-default from settings)
          if (existingDefaultCurrencyRate && accountCurrency !== defaultCurrency) {
            const calculatedAccountToDefaultRate = calculateTransferRate(accountCurrency, defaultCurrency);
            const storedDefaultRate = parseFloat(existingDefaultCurrencyRate);
            if (!isNaN(storedDefaultRate) && storedDefaultRate > 0) {
              const defaultRateDifference = Math.abs(storedDefaultRate - calculatedAccountToDefaultRate) / Math.max(storedDefaultRate, calculatedAccountToDefaultRate);
              if (defaultRateDifference > 0.001) {
                setDefaultCurrencyRateManuallyEdited(true);
              } else {
                setDefaultCurrencyRateManuallyEdited(false);
              }
            } else {
              setDefaultCurrencyRateManuallyEdited(false);
            }
          } else {
            setDefaultCurrencyRateManuallyEdited(false);
          }
          
          const scope = transactionType === "income" ? "income" : "expense";
          let feeAmount: string | undefined;
          let feeAccountId: string | undefined;
          if (transactionType === "expense" && transactions && transactions.length > 0) {
            const feeTx = transactions.find((t: any) => t.expenseFeeFor === transaction.id && t.type === "expense");
            if (feeTx) {
              feeAmount = feeTx.amount?.toString();
              feeAccountId = feeTx.accountId;
            }
          }
          const loadedFormData: FormData = {
            category: categoryName,
            amount: transaction.amount?.toString() || "",
            currency: transactionCurrency,
            rate: existingRate,
            defaultCurrencyRate: existingDefaultCurrencyRate,
            date: formatDateLocal(transactionDate),
            time: displayTime,
            accountId: transaction.accountId || "",
            accountGroup: account ? account.group : "",
            details: transaction.details,
            customFieldValues: mergeNoteAndAttachmentIntoCustomFieldValues(
              transactionCustomFields,
              scope,
              (transaction as any).customFieldValues,
              transaction.note,
              transaction.attachmentUrl ?? null,
              transaction.attachmentName ?? null,
              transaction.attachmentType ?? null
            ),
            customFieldPendingFiles: undefined,
            ...(feeAmount !== undefined && feeAccountId !== undefined ? { feeAmount, feeAccountId } : {}),
          };

          setFormData(loadedFormData);
          setInitialFormData(loadedFormData);
          setCalculatorExpression(loadedFormData.amount || "0");

          // If expense and fee not in transactions yet (e.g. list not loaded), fetch from Firestore
          if (transactionType === "expense" && (feeAmount === undefined || !feeAccountId)) {
            (async () => {
              try {
                const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
                const feeQuery = query(
                  transactionsRef,
                  where("expenseFeeFor", "==", transaction.id),
                  where("type", "==", "expense")
                );
                const feeQuerySnapshot = await getDocs(feeQuery);
                if (!feeQuerySnapshot.empty) {
                  const feeDoc = feeQuerySnapshot.docs[0];
                  const feeData = feeDoc.data();
                  const foundFeeAmount = feeData.amount != null ? String(feeData.amount) : undefined;
                  const foundFeeAccountId = feeData.accountId || undefined;
                  if (foundFeeAmount !== undefined && foundFeeAccountId) {
                    setFormData((prev) => ({ ...prev, feeAmount: foundFeeAmount, feeAccountId: foundFeeAccountId }));
                    setInitialFormData({ ...loadedFormData, feeAmount: foundFeeAmount, feeAccountId: foundFeeAccountId });
                  }
                }
              } catch (error) {
                console.error("Error loading expense fee data:", error);
              }
            })();
          }
        }
    };

    // Use transaction passed from list (tap) so edit screen opens immediately without API wait
    if (initialTransaction && initialTransaction.id === transactionId) {
      setEditingTransaction(initialTransaction);
      populateFormFromTransaction(initialTransaction);
      return;
    }

    const loadTransaction = async (): Promise<void> => {
      try {
        const response = await api.makeAuthenticatedRequest(
          `/workspaces/${workspaceId}/transactions/${transactionId}`,
          { method: "GET" }
        );
        if (!response.ok) throw new Error("Failed to load transaction");
        const transaction = await response.json();
        setEditingTransaction(transaction);
        populateFormFromTransaction(transaction);
      } catch (error) {
        console.error("Error loading transaction:", error);
        alert("Failed to load transaction. Redirecting back...");
        navigate(`/${workspaceSlug}/activity`);
      }
    };

    loadTransaction();
  }, [isEditMode, transactionId, workspaceId, workspaceSlug, defaultCurrency, navigate, transactionType, editingTransaction, initialTransaction, setEditingTransaction, setFormData, setTransferFormData, setSelectedCategoryDetails, setCalendarViewDate, setTimeWasEnabled, setRateManuallyEdited, setDefaultCurrencyRateManuallyEdited, setInitialFormData, setCalculatorExpression, calculateTransferRate, formatDateLocal, formatTimeLocal, setShowTransferFee, transactions]);

  // Update category details when categories are loaded (for expense/income transactions)
  useEffect(() => {
    if (!isEditMode || !editingTransaction || editingTransaction.type === "transfer") return;
    if (expenseCategories.length === 0 && incomeCategories.length === 0) return;

    const categoryName = editingTransaction.category || "";
    const categories = editingTransaction.isIncome ? incomeCategories : expenseCategories;
    const selectedCategory = categories.find((cat) => cat.name === categoryName);
    
    if (selectedCategory) {
      const group = selectedCategory.groupId ? categoryGroups.find((g) => g.id === selectedCategory.groupId) : null;
      const allGroupDetails = group?.details || [];
      const enabledDetailNames = selectedCategory.enabledDetails && Array.isArray(selectedCategory.enabledDetails)
        ? selectedCategory.enabledDetails 
        : [];
      const details = enabledDetailNames.length > 0 
        ? allGroupDetails.filter((detail: TransactionCategoryDetail) => enabledDetailNames.includes(detail.name))
        : [];
      
      setSelectedCategoryDetails(details);
    }
  }, [isEditMode, editingTransaction, expenseCategories, incomeCategories, categoryGroups, setSelectedCategoryDetails]);

  // Update account-related fields when accounts are loaded
  useEffect(() => {
    if (!isEditMode || !editingTransaction || accounts.length === 0) return;
    if (editingTransaction.type === "transfer") return;

    const account = accounts.find(acc => acc.id === editingTransaction.accountId);
    if (account && formData.accountGroup !== account.group) {
      setFormData((prev) => ({
        ...prev,
        accountGroup: account.group,
      }));
    }
  }, [isEditMode, editingTransaction, accounts, formData.accountGroup, setFormData]);
}
